<script setup>
  import ProjectsHelpers from '@/helpers/projects.helpers.ts';
  import { CREATE_ENTITY } from '@/services/account/mutations/account/index.js';
  import { API } from '@/services/index.js';
  import { useGridStore } from '@/stores/grid.js';
  import { useProjectDeliverablesStore } from '@/stores/project/deliverables.store.js';
  import { useProjectDetailsStore } from '@/stores/project/details.store.js';
  import { useProjectStore } from '@/stores/project/index.store.ts';
  import { computed, ref } from 'vue';
  import { useRoute } from 'vue-router';

  const props = defineProps({
    params: {
      type: Object,
    },
  });

  const route = useRoute();
  const openMenu = ref(false);
  const fieldTypeDropdown = ref();
  const isLoading = ref(false);
  const isFormValid = ref(false);
  const fieldName = ref('');
  const fieldType = ref(null);
  const fieldOptions = ref([]);
  const entity_name = computed(() => {
    if (route.name === 'Project-Creator-Details') {
      return 'project_creator';
    }

    if (route.name === 'Project-Deliverables') {
      return 'project_deliverable';
    }

    if (route.name === 'Project-Documents') {
      return 'project_documents';
    }
  });

  function include() {
    return [document.querySelector('.included-add-column-form')];
  }

  const entityFieldTypes = computed(() => {
    return ProjectsHelpers.getEntityMappingFieldTypes().filter((type) => type.disable !== true);
  });

  const formRules = {
    // username: (value) => !!value || 'Username is required',
    fieldName: (value) => {
      if (!value) return 'Column name is required';
      if (!/^[a-zA-Z0-9\s]+$/.test(value)) return 'Column name should only contain letters, numbers, and spaces';
      return true;
    },
    fieldType: (value) => !!value || 'Column type is required',
    fieldOptions: (value) => {
      if (fieldType.value === 'multi-select' || fieldType.value === 'single-select') {
        return (fieldOptions.value.length > 0 && fieldOptions.value[0] !== '') || 'Options are required';
      }
      return true;
    },
  };

  const addField = async () => {
    try {
      isLoading.value = true;
      const account_id = Number(useProjectStore().activeAccount.id);
      const project_id = Number(useProjectStore().currentProjectID);

      const createEntityRes = await API.sendQuery('/graphql/account', CREATE_ENTITY, {
        account_id: account_id,
        project_id: project_id,
        field_name: fieldName.value,
        field_type: fieldType.value,
        entity_name: entity_name.value,
        options: fieldOptions.value.filter((option) => option !== ''),
      });

      if (createEntityRes.data.data.createEntity) {
        const newEntity = createEntityRes.data.data.createEntity;

        // rebuild the project creators grid (forces new custom column to have certain params)
        if (route.name === 'Project-Creator-Details') {
          await useProjectDetailsStore().initProjectCreators(false);
        } else if (route.name === 'Project-Deliverables') {
          await useProjectDeliverablesStore().initProjectDeliverables(false);
        }

        const existingColumnDefs = props.params.api.getColumnDefs(); // Get the current column definitions
        const index = existingColumnDefs.length - 1; // get the index of the second-to-last element
        const newColumnDef = ProjectsHelpers.formatCustomColumn(newEntity);

        // Add the new column to the array
        existingColumnDefs.splice(index, 0, newColumnDef); // Insert the new column at the second-to-last index

        // Update the grid with the new column definitions
        props.params.api.setGridOption('columnDefs', existingColumnDefs);
        await useGridStore().updateFilterDropdownOptions(route.name);
        props.params.api.resetColumnState();
      }
    } catch (error) {
      console.error('Error creating entity', error);
      isLoading.value = false;
    } finally {
      isLoading.value = false;
      fieldName.value = '';
      fieldType.value = null;
      fieldOptions.value = [];
    }
  };

  watchEffect(() => {
    if (fieldType.value === 'multi-select' || fieldType.value === 'single-select') {
      fieldOptions.value = [''];
    }
  });

  const closeMenu = () => {
    openMenu.value = false;
    fieldName.value = '';
    fieldType.value = null;
    fieldOptions.value = [];
  };
</script>

<template>
  <div>
    <v-menu v-model="openMenu" attach="#app" :close-on-content-click="false" location="bottom" transition="slide-y-transition" :persistent="isLoading">
      <template v-slot:activator="{ props }">
        <v-btn v-bind="props" variant="text" block :ripple="false">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <v-card :width="400" max-height="600" class="included-add-column-form tw-p-3">
        <div class="tw-mb-2 tw-text-[0.8rem] tw-font-semibold">Add New Column</div>
        <v-form class="included-add-column-form" ref="form" v-model="isFormValid" @submit.prevent="addField">
          <div class="tw-mt-2 tw-flex tw-flex-col tw-items-start tw-justify-between tw-space-y-2">
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="fieldName"
                :rules="[formRules.fieldName]"
                variant="outlined"
                color="primary-blue"
                label=""
                placeholder="Column Name"
                hide-details="auto"
                density="compact"
                :disabled="isLoading"
              ></v-text-field>
            </div>
            <div class="tw-flex tw-w-full tw-flex-col tw-mb-2">
              <v-select
                class="included-add-column-form"
                ref="fieldTypeDropdown"
                placeholder="Column Type"
                color="primary-blue"
                v-model="fieldType"
                :items="entityFieldTypes"
                item-title="label"
                item-value="value"
                :rules="[formRules.fieldType]"
                :disabled="isLoading"
                variant="outlined"
                density="compact"
              >
                <template v-slot:selection="{ item, index, props }">
                  <div style="display: flex"><img :src="ProjectsHelpers.getColumnIcon(item.raw?.icon)" alt="icon" style="margin-right: 5px" /> {{ item.raw.label }}</div>
                </template>
                <template v-slot:item="{ item, props }">
                  <v-list-item class="included-add-column-form" v-bind="props">
                    <template v-slot:prepend>
                      <img :src="ProjectsHelpers.getColumnIcon(item.raw?.icon)" alt="icon" style="margin-right: 1rem" />
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </div>
            <div class="tw-flex tw-w-full tw-flex-col tw-space-y-2" v-if="fieldType === 'multi-select' || fieldType === 'single-select'" style="margin-top: 1rem !important">
              <template v-for="(option, index) in fieldOptions" :key="index">
                <div class="tw-flex tw-items-center tw-justify-between">
                  <v-text-field
                    v-model="fieldOptions[index]"
                    max-width="255px"
                    :rules="[formRules.fieldOptions]"
                    placeholder="Add Option"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    :disabled="isLoading"
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        color="red"
                        icon="mdi-close"
                        variant="text"
                        density="compact"
                        @click="fieldOptions.length === 1 ? (fieldOptions = ['']) : fieldOptions.splice(index, 1)"
                        :disabled="isLoading"
                      ></v-btn>
                    </template>
                  </v-text-field>
                </div>
              </template>
              <v-btn class="tw-capitalize tw-mt-2" color="primary-blue" variant="text" density="compact" @click="fieldOptions.push('')" icon="mdi-plus" :disabled="isLoading"></v-btn>
            </div>
          </div>
          <div class="tw-mt-10 tw-flex tw-w-full tw-items-center tw-justify-between">
            <v-btn class="tw-capitalize" variant="tonal" size="small" height="40px" @click="closeMenu" :disabled="isLoading"> Cancel </v-btn>
            <v-btn class="tw-capitalize" color="primary-blue" variant="tonal" size="small" height="40px" type="submit" :loading="isLoading" :disabled="isLoading || !isFormValid">
              Create Column
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-menu>
  </div>
</template>

<style scoped></style>
